import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router";

import { getErrorMessage } from "helpers";
import {
  getRecipe,
  Recipe,
  initialRecipe,
  putRecipe,
  postRecipe,
  deleteRecipe,
  publishRecipe,
} from "services/recipes";
import { Category, getCategories } from "services/categories";
import { Image } from "services/images";

export const useRecipePage = (id: number) => {
  const history = useHistory();
  const [formData, setFormData] = useState<Recipe>(initialRecipe);
  const [categoryOptions, setCategoryOptions] = useState<Category[]>([]);

  // Fetch data
  useEffect(() => {
    (async () => {
      if (id > 0) {
        try {
          const data = await getRecipe(id);
          setFormData(data);
        } catch (e) {
          toast.error(getErrorMessage(e));
          console.error(e);
        }
      }
    })();
  }, [id]);

  // Fetch categories
  useEffect(() => {
    (async () => {
      try {
        const data = await getCategories();
        setCategoryOptions(data);
      } catch (e) {
        toast.error(getErrorMessage(e));
        console.error(e);
      }
    })();
  }, [id]);

  const inputChanged = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    setFormData((old) => ({ ...old, [name]: value }));
  };

  const onFileChange = (image: Image | null) => {
    setFormData((old) => ({ ...old, image }));
  };

  const onSelectChange = (category: any) => {
    setFormData((old) => ({ ...old, category }));
  };

  const onRichTextChange = (value: string, name: string) => {
    setFormData((old) => ({ ...old, [name]: value }));
  };

  const onFormSave = async (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();

    // POST
    if (formData.id < 0) {
      try {
        const newRecipe = await postRecipe(formData);
        history.push(`/recipes/${newRecipe.id}`);
        toast.success("Recipe saved.");
      } catch (e) {
        toast.error(getErrorMessage(e));
        console.error(e);
      }
      return;
    }

    // PUT
    try {
      const newRecipe = await putRecipe(formData);
      setFormData(newRecipe);
      toast.success("Recipe saved.");
    } catch (e) {
      toast.error(getErrorMessage(e));
      console.error(e);
    }
  };

  const onPublishClick = async () => {
    try {
      const newRecipe = await publishRecipe(formData.id, !formData.published);
      setFormData(newRecipe);
      toast.success("Recipe published.");
    } catch (e) {
      toast.error(getErrorMessage(e));
      console.error(e);
    }
  };

  const onDeleteRecipe = async () => {
    try {
      await deleteRecipe(id);
      toast.success("Recipe deleted.");
      history.push("/recipes");
    } catch (e) {
      toast.error(getErrorMessage(e));
      console.error(e);
    }
  };

  return {
    formData,
    categoryOptions,
    onDeleteRecipe,
    inputChanged,
    onFileChange,
    onFormSave,
    onSelectChange,
    onRichTextChange,
    onPublishClick,
  };
};
